import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import Button from "../../components/Button";
import Filter from "../../components/Filter";
import theme from "../../theme";
import {
  getFilteredItems,
  getHighlightedItems,
  getNonHighlightedItems,
} from "./utils";

export const ALL_FILTER_KEY = "magic-unique-all-filter-id";

const DEFAULT_CONFIG = {
  itemAmount: 4,
  itemIncrement: 4,
  moreButtonText: "Toon meer",
  filterAllLabel: "Alles",
  noItemsText: "Geen items gevonden.",
};

export const configType = {
  itemAmount: PropTypes.number,
  itemIncrement: PropTypes.number,
  filterAllLabel: PropTypes.string,
  moreButtonText: PropTypes.string,
  noItemsText: PropTypes.string,
};

const MoreWrapper = styled.div`
  position: relative;
  z-index: 100;
  display: flex;
  justify-content: center;
  margin: ${theme.space("xl")} 0;
`;

const StyledFilter = styled(Filter)`
  margin-bottom: ${theme.space("xl")};
  padding-left: 0;
`;

function useItemFilter(
  items,
  filters,
  activeFilter,
  onFilterChange = (f) => f,
  highlights,
  config = {}
) {
  // Apply defaults to optionally partial config.
  const _config = {
    itemAmount: config.itemAmount || DEFAULT_CONFIG.itemAmount,
    itemIncrement: config.itemIncrement || DEFAULT_CONFIG.itemIncrement,
    moreButtonText: config.moreButtonText || DEFAULT_CONFIG.moreButtonText,
    filterAllLabel: config.filterAllLabel || DEFAULT_CONFIG.filterAllLabel,
    noItemsText: config.noItemsText || DEFAULT_CONFIG.noItemsText,
  };

  // Adding an 'All' filter that stand for 'no filter'.
  const actualFilters = [
    { id: ALL_FILTER_KEY, label: _config.filterAllLabel },
    ...filters,
  ];

  const [currentAmount, setCurrentAmount] = useState(_config.itemAmount);
  const [currentFilter, setCurrentFilter] = useState(
    activeFilter || actualFilters[0]
  );

  let filteredItems = [...items];
  let highlighted = [];

  useEffect(() => {
    let isCanceled = false;

    // Reinitialize the cases amount when the filter changes.
    if (!isCanceled) {
      setCurrentAmount(_config.itemAmount);
    }

    return () => {
      isCanceled = true;
    };
  }, [currentFilter, _config.itemAmount]);

  // Apply a filter on the items when a filter is present.
  if (currentFilter && currentFilter.id !== ALL_FILTER_KEY) {
    filteredItems = getFilteredItems(filteredItems, currentFilter);
  }

  // Seperate the items in highlighted and non-highlighted.
  // Highlighted cases are disallowed when filtering.
  if (
    highlights &&
    (!currentFilter || (currentFilter && currentFilter.id === ALL_FILTER_KEY))
  ) {
    highlighted = getHighlightedItems(filteredItems);
    filteredItems = getNonHighlightedItems(filteredItems);
  }

  // Grab the first n items.
  const itemsByAmount = [...filteredItems].splice(0, currentAmount);

  return {
    items: itemsByAmount,
    highlighted,
    filter: currentFilter,
    filterElement: (
      <StyledFilter
        items={actualFilters}
        activeFilter={currentFilter}
        onChange={(newFilter) => {
          onFilterChange(newFilter);
          setCurrentFilter(newFilter);
        }}
      />
    ),
    moreElement:
      filteredItems.length > currentAmount ? (
        <MoreWrapper>
          <Button
            onClick={() =>
              setCurrentAmount(currentAmount + _config.itemIncrement)
            }
          >
            {_config.moreButtonText}
          </Button>
        </MoreWrapper>
      ) : null,
    noItems: filteredItems.length === 0 ? _config.noItemsText : "",
  };
}

export default useItemFilter;
